import { useEffect, useState } from 'react';
import { HexColorInput, HexColorPicker } from 'react-colorful';
import { useNavigate, useParams } from 'react-router-dom';
import { useWindowSize } from 'react-use';

import {
  Autosuggest,
  Badge,
  Box,
  Button,
  Checkbox,
  ContentLayout,
  Form,
  FormField,
  Header,
  Input,
  Modal,
  SpaceBetween,
  Textarea,
} from '@cloudscape-design/components';

import { fetchBatch, updateBatch } from '../api/batches';
import { createItem, deleteItem, fetchItemsWithFilter, omitUndefinedDeep, updateItem } from '../api/generic';
import DisplayImage from '../components/DisplayImage';
import ErrorBar from '../components/ErrorBar';
import Flex, { Space } from '../components/Flex';
import Loader from '../components/Loader';
import StepManager from '../components/StepManager';
import UploadImage from '../components/UploadImage';
import { useAuthContext } from '../contexts/AuthContext';
import useSubscribe from '../hooks/useSubscribe';
import { Batch } from '../models/Batch';
import { InventoryItem } from '../models/Inventory';
import { PerContainerComponent, Recipe, RecipeContainer, Step } from '../models/Recipe';
import { prefixCollection } from '../utils/prefix';
import { sortName } from '../utils/sort';
import { slugify } from '../utils/string';
import {
  removeIngredient as removeIngredientLogic,
  shiftIngredientDown as shiftIngredientDownLogic,
  shiftIngredientUp as shiftIngredientUpLogic,
} from './RecipePage.logic';

export const initalRecipe: Recipe = {
  id: '',
  name: '',
  ingredients: [],
  steps: [],
  style: '',
  batchSize: 0,
  slug: '',
};

interface StepWithId extends Step {
  id: string;
}

const RecipePage = () => {
  const { tenant } = useAuthContext();
  const { width } = useWindowSize();
  const isMobile = width <= 767;
  const { id: idParam, recipe: recipeParam } = useParams<{ id?: string; recipe?: string }>();
  const inBatch = Boolean(recipeParam);
  const batchId = inBatch ? idParam : undefined;
  const recipeId = !inBatch ? idParam : undefined;
  const navigate = useNavigate();

  const [ingredients, setIngredients] = useState<InventoryItem[]>([]);
  const [error, setError] = useState<Error>();
  const [item, setItem] = useState<Recipe>();
  const [batch, setBatch] = useState<Batch | null>(null);
  const [validate, setValidate] = useState<boolean>(false);

  const [batchSize, setBatchSize] = useState<string>('');
  const [, setDefaultAbv] = useState<string>('');

  const [visibleI, setVisibleI] = useState(false);
  const [editI, setEditI] = useState(-1);
  const [addIngredient, setAddIngredient] = useState('');
  const [addQuantity, setAddQuantity] = useState('');

  const [recipes, setRecipes] = useState<Recipe[]>();
  const [steps, setSteps] = useState<StepWithId[]>();

  const [defaultAbvTxt, setDefaultAbvTxt] = useState<string>('');

  const [stepQ, setStepQ] = useState('');
  const [stepQItems, setStepQItems] = useState<{ value: string; label: string; type: string }[]>();

  // Container modal
  const [visibleC, setVisibleC] = useState(false);
  const [editCIndex, setEditCIndex] = useState<number | null>(null);

  // Container form fields
  const [containerItemId, setContainerItemId] = useState<string>('');
  const [sizeL, setSizeL] = useState<string>('');

  // Per-container components (in the container modal)
  const [components, setComponents] = useState<PerContainerComponent[]>([]);
  const [compItemId, setCompItemId] = useState<string>('');
  const [compAmount, setCompAmount] = useState<string>('');
  const [compUnitOverride, setCompUnitOverride] = useState<string>('');

  // Inventory filters
  const containerItems = ingredients.filter((i) => i.isContainer);
  const perContainerItems = ingredients.filter((i) => i.perContainer);

  const [isRecipeValid, setIsRecipeValid] = useState(false);

  // Safe item name
  const nameOf = (id?: string) => ingredients.find((i) => i.id === id)?.name ?? id ?? '';

  useSubscribe<Recipe>(
    prefixCollection('recipes', tenant),
    (items) => {
      if (!inBatch && recipeId) {
        const findItem = items.find((x) => x.id === recipeId);
        setItem(findItem);
      }
      setRecipes(items);
    },
    setError,
    sortName,
    undefined,
    true,
    [inBatch, recipeId],
  );

  useSubscribe<InventoryItem>(prefixCollection('inventory', tenant), setIngredients, setError, sortName);

  useEffect(() => {
    if (inBatch && batchId) {
      fetchBatch(batchId, tenant).then((b) => {
        setBatch(b);
        setItem(b?.recipe ?? undefined);
        setBatchSize(b?.recipe?.batchSize?.toString() ?? '');
        setDefaultAbv(b?.recipe?.defaultAbv?.toString() ?? '');
        if (!defaultAbvTxt) setDefaultAbvTxt(b?.recipe?.defaultAbv?.toString() ?? '');
        if (b === null) navigate('/batches/' + batchId);
      });
    }
  }, [inBatch, batchId, tenant, navigate]);

  const handleStepImport = (stepOption: any) => {
    const idParts = stepOption.value.split('#');
    const step = recipes?.find((r) => r.id === idParts[0])?.steps.find((s) => s.name === idParts[1]);
    if (step) {
      setItem((x) => {
        if (!x) return x;

        const { steps } = x;

        return { ...x, steps: [...steps, step] };
      });
    }
    setStepQ('');
  };

  const handleRecipes = (stepOption: any) => {
    const recipeToAdd = recipes?.find((r) => r.id === stepOption.value);
    if (recipeToAdd && recipeToAdd.steps) {
      const stepsToAdd = recipeToAdd.steps;

      setItem((currentRecipe) => {
        if (!currentRecipe) return;
        const { steps } = currentRecipe;
        return {
          ...currentRecipe,
          steps: [...steps, ...stepsToAdd],
        };
      });
    }
    setStepQ('');
  };

  const handleSelect = (stepOption: any) => {
    switch (stepOption.type) {
      case 'step':
        handleStepImport(stepOption);
        break;
      case 'recipe':
        handleRecipes(stepOption);
        break;
    }
  };

  const openAddContainer = () => {
    setEditCIndex(null);
    setContainerItemId('');
    setSizeL('');
    setComponents([]);
    setCompItemId('');
    setCompAmount('');
    setCompUnitOverride('');
    setVisibleC(true);
  };

  const openEditContainer = (idx: number) => {
    const c = item?.containers?.[idx];
    if (!c) return;
    setEditCIndex(idx);
    setContainerItemId(c.containerItem.id);
    setSizeL(String(c.sizeL ?? c.containerItem.capacityL ?? 0));
    setComponents(c.components ?? []);
    setVisibleC(true);
  };

  const closeContainerModal = () => {
    setVisibleC(false);
    setEditCIndex(null);
  };

  const pushContainer = () => {
    if (!item) return;
    const ci = ingredients.find((i) => i.id === containerItemId);
    if (!ci) return;

    const newContainer: RecipeContainer = {
      containerItem: ci,
      sizeL: parseFloat(sizeL || `${ci.capacityL ?? 0}`),
      components: components ?? [],
    };

    setItem((prev) => {
      if (!prev) return prev;
      const current = prev.containers ?? [];
      if (editCIndex === null) return { ...prev, containers: [...current, newContainer] };
      const copy = [...current];
      copy[editCIndex] = newContainer;
      return { ...prev, containers: copy };
    });

    closeContainerModal();
  };

  const addComponent = () => {
    const it = ingredients.find((i) => i.id === compItemId);
    if (!it) return;
    const amt = parseFloat(compAmount || '0');
    if (Number.isNaN(amt) || amt <= 0) return;

    setComponents((prev) => [
      ...(prev ?? []),
      {
        item: it,
        amountPerContainer: amt,
        unitOverride: compUnitOverride || undefined,
      },
    ]);

    setCompItemId('');
    setCompAmount('');
    setCompUnitOverride('');
  };

  const removeComponent = (idx: number) => {
    setComponents((prev) => (prev ?? []).filter((_, i) => i !== idx));
  };

  const removeContainer = (idx: number) => {
    setItem((prev) => {
      if (!prev) return prev;
      const current = prev.containers ?? [];
      return { ...prev, containers: current.filter((_, i) => i !== idx) };
    });
  };

  useEffect(() => {
    if (recipes) {
      const stepsFromRecipes: StepWithId[] = recipes.flatMap((r) =>
        r.steps.map((s) => {
          return { id: `${r.id}#${s.name}`, ...s };
        }),
      );

      setSteps(stepsFromRecipes);
    }
  }, [recipes]);

  useEffect(() => {
    if (steps && recipes) {
      const options = steps.map((s) => {
        return { value: s.id, label: s.name, type: 'step' };
      });

      const rOptions = recipes.map((r) => {
        return { value: r.id, label: `${r.name} (Recipe)`, type: 'recipe' };
      });

      setStepQItems([...options, ...rOptions]);
    }
  }, [steps]);

  useEffect(() => {
    if (inBatch || !recipeId || recipeId === 'new') return;
    setBatchSize(item?.batchSize.toString() ?? '');
    setDefaultAbv(item?.defaultAbv?.toString() ?? '');
    if (!defaultAbvTxt && defaultAbvTxt !== '') setDefaultAbvTxt(item?.defaultAbv?.toString() ?? '');
  }, [item, inBatch, recipeId]);

  const addRecipe = async (item: Omit<Recipe, 'id'>) => {
    const newId = await createItem<Recipe>(prefixCollection('recipes', tenant), item);
    return newId;
  };

  const updateRecipe = async (item: Recipe) => {
    await updateItem<Recipe>(prefixCollection('recipes', tenant), item.id, item);
  };

  useEffect(() => {
    setIsRecipeValid(
      Boolean(item?.name && item?.style && defaultAbvTxt && item?.batchSize && item?.slug && item?.description),
    );
  }, [item]);

  const submitForm = async (stay = false) => {
    setValidate(true);
    if (!isRecipeValid || !item) return;

    const clean = omitUndefinedDeep({
      ...item,
      defaultAbv: defaultAbvTxt ? parseFloat(defaultAbvTxt) : item.defaultAbv,
    }) as Recipe;

    if (inBatch && batch && batchId) {
      await updateBatch(batch.id, { ...batch, recipe: clean, recipeSlug: clean.slug }, tenant);
      if (!stay) navigate('/batches/' + batchId);
    } else {
      if (recipeId === 'new') {
        setWaiting(true);
        const newRecipe = await addRecipe(clean);
        if (newRecipe) {
          setTimeout(() => {
            navigate('/recipes' + (stay ? '/' + newRecipe.id : ''));
            setWaiting(false);
          }, 3000);
        }
      } else await updateRecipe(clean);
      if (!stay) navigate('/recipes');
    }
  };

  const handleDeleteRecipe = async () => {
    if (!recipeId || recipeId === 'new') return;
    if (confirm('Are you sure you want to delete this recipe?')) {
      await deleteItem(prefixCollection('recipes', tenant), recipeId);
      navigate('/recipes');
    }
  };

  const handleGetStepsFromRecipe = async () => {
    if (!inBatch || !batch?.recipeId) return;
    const r = await fetchItemsWithFilter<Recipe>(prefixCollection('recipes', tenant), [
      { field: 'id', operator: '==', value: batch.recipeId },
    ]);
    const steps = r[0]?.steps ?? [];
    setItem((prev) => (prev ? { ...prev, steps } : prev));
  };

  const handleEditIngredient = (i: number) => {
    setEditI(i);

    if (i >= 0) {
      const ingredient = item?.ingredients[i];
      setAddIngredient(ingredient?.item.id ?? '');
      setAddQuantity(ingredient?.quantity?.toString() ?? '');
    }
    setVisibleI(true);
  };

  const shiftIngredientUp = (ingredientIndex: number) => {
    setItem((prevItem) => (prevItem ? shiftIngredientUpLogic(prevItem, ingredientIndex) : initalRecipe));
  };

  const shiftIngredientDown = (ingredientIndex: number) => {
    setItem((prevItem) => (prevItem ? shiftIngredientDownLogic(prevItem, ingredientIndex) : initalRecipe));
  };
  const removeIngredient = (ingredientIndex: number) => {
    setItem((prevItem) => (prevItem ? removeIngredientLogic(prevItem, ingredientIndex) : initalRecipe));
  };

  const handleCopy = async () => {
    if (inBatch) return;
    setValidate(true);
    if (!isRecipeValid || !item) return;

    const newId = (
      await createItem<Recipe>(prefixCollection('recipes', tenant), {
        ...item,
        name: item.name + ' (copy)',
      })
    ).id;
    if (newId) navigate('/recipes');
  };

  const handleAddIngredient = async () => {
    if (!item || !item.ingredients || !item.ingredients[editI]?.item) {
      console.warn('Invalid item or ingredient at index', editI);
      return;
    }

    const itemToAdd = item.ingredients[editI].item as Omit<InventoryItem, 'id'>;

    try {
      const response = await createItem<InventoryItem>(prefixCollection('inventory', tenant), itemToAdd);

      const updatedItem = { ...item };
      updatedItem.ingredients = [...updatedItem.ingredients];
      updatedItem.ingredients[editI] = {
        ...updatedItem.ingredients[editI],
        item: response,
      };

      setItem(updatedItem);

      if (inBatch && batch && batchId) {
        const updatedBatch = { ...batch, recipe: updatedItem };
        await updateBatch(batch.id, updatedBatch, tenant);
        setBatch(updatedBatch);
      } else {
        await updateRecipe(updatedItem);
      }

      setVisibleI(false);
      setEditI(-1);
      setAddIngredient('');
      setAddQuantity('');
    } catch (err) {
      console.error('Error creating item:', err);
    }
  };

  const [waiting, setWaiting] = useState(false);

  return (
    <Loader isLoading={waiting} size="large">
      <ContentLayout
        disableOverlap
        header={
          <SpaceBetween direction="horizontal" size="m">
            {!inBatch && (
              <DisplayImage
                fileName="image"
                filePath={'recipes/' + (recipeId ?? '')}
                round
                altText="Recipe image"
                style={{ height: 50, width: 50 }}
                hide={isMobile}
              />
            )}
            <Header
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  {inBatch ? (
                    <>
                      <Button onClick={() => window.history.back()} formAction="none" variant="link">
                        Cancel
                      </Button>
                      <Button
                        onClick={() => submitForm()}
                        formAction="none"
                        variant="primary"
                        disabled={!isRecipeValid}
                      >
                        Submit
                      </Button>
                      <Button
                        onClick={() => submitForm(true)}
                        formAction="none"
                        variant="primary"
                        disabled={!isRecipeValid}
                      >
                        Save
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button onClick={() => navigate('print')} variant="icon" iconName="file" />
                      <Button
                        onClick={() => window.history.back()}
                        formAction="none"
                        variant="normal"
                        iconName="arrow-left"
                      >
                        Back
                      </Button>
                      <Button
                        onClick={() => submitForm()}
                        formAction="none"
                        variant="primary"
                        disabled={!isRecipeValid}
                      >
                        Submit
                      </Button>
                      <Button
                        onClick={() => submitForm(true)}
                        formAction="none"
                        variant="primary"
                        disabled={!isRecipeValid}
                      >
                        Save
                      </Button>
                      <Button onClick={() => handleCopy()} variant="normal">
                        Copy
                      </Button>
                      <Button onClick={() => handleDeleteRecipe()} formAction="none" variant="normal">
                        Delete
                      </Button>
                    </>
                  )}
                </SpaceBetween>
              }
              variant="h1"
              description={
                inBatch ? 'Make changes to batch recipe' : recipeId === 'new' ? 'Create new recipe' : recipeId
              }
            >
              {inBatch
                ? (item?.name ?? '...')
                : recipeId === 'new'
                  ? 'Creating new recipe'
                  : (item?.name ?? 'Loading...')}
            </Header>
          </SpaceBetween>
        }
      >
        <div style={{ marginTop: 10 }}>
          <ErrorBar error={error} setError={setError} />
          <Form>
            <SpaceBetween direction="vertical" size="l">
              <FormField label="Name">
                <Input
                  readOnly={inBatch}
                  invalid={validate && !item?.name}
                  value={item?.name ?? ''}
                  onChange={(event) => {
                    const newValue = event.detail.value;

                    setItem((prevItem) => {
                      if (!prevItem) {
                        return {
                          id: '',
                          name: newValue,
                          ingredients: [],
                          steps: [],
                          style: '',
                          batchSize: 0,
                          slug: '',
                        };
                      } else {
                        return { ...prevItem, name: newValue };
                      }
                    });
                  }}
                />
              </FormField>
              <FormField label="Style">
                <Input
                  readOnly={inBatch}
                  invalid={validate && !item?.style}
                  value={item?.style ?? ''}
                  onChange={(event) => {
                    const newValue = event.detail.value;

                    setItem((prevItem) => {
                      if (!prevItem) {
                        return {
                          id: '',
                          name: '',
                          ingredients: [],
                          steps: [],
                          style: newValue,
                          batchSize: 0,
                          slug: '',
                        };
                      } else {
                        return { ...prevItem, style: newValue };
                      }
                    });
                  }}
                />
              </FormField>

              <Flex align="start" style={{ width: '100%' }}>
                <FormField label="Expected ABV">
                  <Input
                    invalid={validate && !defaultAbvTxt}
                    value={defaultAbvTxt ?? ''}
                    onChange={(event) => {
                      const newValue = event.detail.value;
                      console.log('newValue', newValue);
                      setDefaultAbvTxt(newValue);
                      setDefaultAbv(newValue);

                      setItem((prevItem) => {
                        if (!prevItem) {
                          return {
                            id: '',
                            name: '',
                            ingredients: [],
                            steps: [],
                            style: '',
                            batchSize: 0,
                            slug: '',
                            defaultAbv: parseFloat(newValue),
                          };
                        } else {
                          return { ...prevItem, defaultAbv: parseFloat(newValue) };
                        }
                      });
                    }}
                  />
                </FormField>

                <FormField label="Batch size (L)">
                  <Input
                    invalid={validate && !item?.batchSize}
                    value={batchSize}
                    onChange={(event) => {
                      const newValue = event.detail.value;
                      setBatchSize(newValue);

                      setItem((prevItem) => {
                        if (!prevItem) {
                          return {
                            id: '',
                            name: '',
                            ingredients: [],
                            steps: [],
                            style: '',
                            batchSize: parseFloat(newValue),
                            slug: '',
                          };
                        } else {
                          return { ...prevItem, batchSize: parseFloat(newValue) };
                        }
                      });
                    }}
                  />
                </FormField>

                <FormField label="Product (slug)">
                  <Flex gap={0}>
                    <Input
                      invalid={validate && !item?.slug}
                      value={item?.slug ?? ''}
                      onChange={(event) => {
                        const newValue = slugify(event.detail.value);

                        setItem((prevItem) => {
                          if (!prevItem) {
                            return {
                              id: '',
                              name: '',
                              ingredients: [],
                              steps: [],
                              style: '',
                              batchSize: 0,
                              slug: newValue,
                            };
                          } else {
                            return { ...prevItem, slug: newValue };
                          }
                        });
                      }}
                    />
                    <Button
                      variant="icon"
                      iconName="gen-ai"
                      onClick={() =>
                        setItem((prevItem) => {
                          if (!prevItem) {
                            //
                          } else {
                            return { ...prevItem, slug: slugify(prevItem.name) };
                          }
                        })
                      }
                    />
                  </Flex>
                </FormField>
              </Flex>

              <FormField label="Description">
                <Textarea
                  invalid={validate && !item?.description}
                  value={item?.description ?? ''}
                  onChange={(event) => {
                    const newValue = event.detail.value;

                    setItem((prevItem) => {
                      if (!prevItem) {
                        return {
                          id: '',
                          name: '',
                          ingredients: [],
                          steps: [],
                          style: '',
                          batchSize: 0,
                          slug: '',
                        };
                      } else {
                        return {
                          ...prevItem,
                          description: newValue,
                        };
                      }
                    });
                  }}
                />
              </FormField>

              <FormField label="Experimental">
                <Checkbox
                  checked={item?.experimental ?? false}
                  onChange={({ detail }) =>
                    setItem((prev) =>
                      prev
                        ? { ...prev, experimental: detail.checked }
                        : { ...initalRecipe, experimental: detail.checked },
                    )
                  }
                >
                  Mark this recipe as experimental
                </Checkbox>
              </FormField>

              <FormField label="Product tint color">
                <Space h={8} />
                <SpaceBetween direction="horizontal" size="m">
                  <HexColorPicker
                    color={item?.color ?? '#000000'}
                    onChange={(hex) =>
                      setItem((prev) => (prev ? { ...prev, color: hex } : { ...initalRecipe, color: hex }))
                    }
                    // react-colorful returns a string hex directly, not { hex }
                  />
                  <HexColorInput
                    color={item?.color ?? '#000000'}
                    onChange={(hex) =>
                      setItem((prev) => (prev ? { ...prev, color: hex.startsWith('#') ? hex : `#${hex}` } : prev))
                    }
                    prefixed
                    style={{ width: 140 }}
                  />
                </SpaceBetween>
              </FormField>

              {!inBatch && recipeId !== 'new' && (
                <UploadImage
                  key={'ri_upload'}
                  title="Recipe image"
                  fileName="image"
                  filePath={'recipes/' + (recipeId ?? '')}
                  round
                />
              )}

              <div style={{ marginTop: 10 }}>
                <SpaceBetween size="m">
                  <Button
                    onClick={() => {
                      setVisibleI(true);
                      setEditI(-1);
                      setAddIngredient('');
                      setAddQuantity('');
                    }}
                  >
                    Add ingredients
                  </Button>
                  {item?.ingredients.map((ingredient, i) => {
                    return (
                      <SpaceBetween key={ingredient.item.id + '-' + i} size="m" direction="horizontal">
                        <Button
                          iconName="angle-up"
                          disabled={i === 0}
                          onClick={() => shiftIngredientUp(i)}
                          variant="inline-link"
                        />

                        <Button
                          disabled={i === item.ingredients.length - 1}
                          iconName="angle-down"
                          onClick={() => shiftIngredientDown(i)}
                          variant="inline-link"
                        />

                        <span>
                          {ingredient.item?.name ?? ingredient.item.id} {ingredient.quantity} {ingredient.item.unit}
                        </span>
                        <Button onClick={() => handleEditIngredient(i)} variant="inline-link">
                          Edit
                        </Button>
                        <Button onClick={() => removeIngredient(i)} variant="inline-link">
                          Remove
                        </Button>
                      </SpaceBetween>
                    );
                  })}
                </SpaceBetween>
              </div>

              {/* --- Packaging / Containers --- */}
              <Box margin={{ top: 'l' }}>
                <Button onClick={openAddContainer} iconName="add-plus">
                  Add container
                </Button>

                {!item?.containers?.length ? (
                  <Box color="text-body-secondary">No containers yet.</Box>
                ) : (
                  <SpaceBetween size="s">
                    {item!.containers!.map((c, idx) => (
                      <Box key={c.containerItem.id + '-' + idx} padding="s">
                        <SpaceBetween size="xs" direction="horizontal">
                          <Badge>Container</Badge>
                          <strong>{c.containerItem.name}</strong>
                          <span>{c.sizeL} L</span>

                          <Button variant="inline-link" onClick={() => openEditContainer(idx)}>
                            Edit
                          </Button>
                          <Button variant="inline-link" onClick={() => removeContainer(idx)}>
                            Remove
                          </Button>
                        </SpaceBetween>

                        {/* Components */}
                        <Box margin={{ top: 'xs' }}>
                          {!c.components?.length ? (
                            <i>No per-container components</i>
                          ) : (
                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                              {c.components.map((pc, i2) => (
                                <li key={pc.item.id + '-' + i2}>
                                  {pc.amountPerContainer} {pc.unitOverride ?? pc.item.unit} × {pc.item.name}
                                </li>
                              ))}
                            </ul>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </SpaceBetween>
                )}
              </Box>

              {inBatch && <Button onClick={handleGetStepsFromRecipe}>Get steps from recipe</Button>}
              <StepManager
                steps={item?.steps ?? []}
                stepOptions={inBatch ? [] : (stepQItems ?? [])}
                onChange={(newSteps) => setItem((prev) => (prev ? { ...prev, steps: newSteps } : prev))}
                onImportStep={handleSelect}
              />
            </SpaceBetween>
          </Form>
          <Modal
            onDismiss={() => setVisibleI(false)}
            visible={visibleI}
            footer={
              <Box float="right">
                <SpaceBetween direction="horizontal" size="xs">
                  <Button
                    onClick={() => {
                      setVisibleI(false);
                      setAddIngredient('');
                      setEditI(-1);
                      setAddQuantity('');
                    }}
                    variant="link"
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={
                      !ingredients.find((i) => i.id === item?.ingredients[editI]?.item.id) &&
                      !ingredients.find((i) => i.id === addIngredient)
                    }
                    variant="primary"
                    onClick={() => {
                      if (item && editI >= 0) {
                        const newItem = { ...item };
                        const newIngredients = [
                          ...item.ingredients.slice(0, editI), // Include ingredients before the index
                          ...item.ingredients.slice(editI + 1), // Include ingredients after the index
                          {
                            item: ingredients.find((i) => i.id === addIngredient)!,
                            quantity: parseFloat(addQuantity),
                          },
                        ];
                        // Assuming you have some code to update `item.ingredients` or to do something with `newIngredients`
                        newItem.ingredients = newIngredients;
                        setItem(newItem);
                        setVisibleI(false);
                        setAddIngredient('');
                        setAddQuantity('');
                      } else if (addIngredient && addQuantity) {
                        setItem((prevItem) => {
                          if (!prevItem) {
                            return {
                              id: '',
                              name: '',
                              ingredients: [
                                {
                                  item: ingredients.find((i) => i.id === addIngredient)!,
                                  quantity: parseFloat(addQuantity),
                                },
                              ],
                              steps: [],
                              style: '',
                              batchSize: 0,
                              slug: '',
                            };
                          } else {
                            return {
                              ...prevItem,
                              ingredients: [
                                ...prevItem.ingredients,
                                {
                                  item: ingredients.find((i) => i.id === addIngredient)!,
                                  quantity: parseFloat(addQuantity),
                                },
                              ],
                            };
                          }
                        });
                        setVisibleI(false);
                        setAddIngredient('');
                        setAddQuantity('');
                      }
                    }}
                  >
                    Ok
                  </Button>
                </SpaceBetween>
              </Box>
            }
            header={editI ? 'Edit ingredient' : 'Add ingredients'}
          >
            <SpaceBetween direction="vertical" size="l">
              {editI >= 0 ? (
                <>
                  <Box>You are editing an ingredient. To change the type, remove it and add a new type.</Box>
                  {ingredients.find((i) => i.id === item?.ingredients[editI]?.item.id) ? (
                    ''
                  ) : (
                    <Flex align="center" justify="start">
                      <Box>This item type is not in your inventory, add it?</Box>
                      <Button onClick={handleAddIngredient} variant="inline-link">
                        Add it +
                      </Button>
                    </Flex>
                  )}
                </>
              ) : (
                ''
              )}

              <Autosuggest
                onChange={({ detail }) => setAddIngredient(detail.value)}
                value={ingredients.find((x) => x.id === addIngredient)?.name ?? addIngredient}
                options={ingredients.map((ingredient) => ({
                  value: ingredient.id,
                  label: ingredient.name,
                }))}
                ariaLabel="Autosuggest example with values and labels"
                placeholder="Enter value"
                empty="No matches found"
              />
              <Input
                placeholder="Quantity"
                value={addQuantity}
                onChange={({ detail }) => setAddQuantity(detail.value)}
              />
              {addIngredient && (
                <strong style={{ fontSize: 18 }}>
                  {isNaN(parseFloat(addQuantity)) ? '0' : parseFloat(addQuantity)}
                  {ingredients.find((i) => i.id === addIngredient)?.unit} of{' '}
                  {ingredients.find((i) => i.id === addIngredient)?.name}
                </strong>
              )}
            </SpaceBetween>
          </Modal>
          <Modal
            visible={visibleC}
            onDismiss={closeContainerModal}
            header={editCIndex === null ? 'Add container' : 'Edit container'}
            footer={
              <Box float="right">
                <SpaceBetween direction="horizontal" size="xs">
                  <Button variant="link" onClick={closeContainerModal}>
                    Cancel
                  </Button>
                  <Button variant="primary" onClick={pushContainer}>
                    Save
                  </Button>
                </SpaceBetween>
              </Box>
            }
          >
            <SpaceBetween size="l">
              <FormField label="Container item">
                <Autosuggest
                  onChange={({ detail }) => {
                    setContainerItemId(detail.value);
                    const it = ingredients.find((i) => i.id === detail.value);
                    // If empty size, default from capacityL
                    if (it?.capacityL && !sizeL) setSizeL(String(it.capacityL));
                  }}
                  value={nameOf(containerItemId)}
                  options={containerItems.map((i) => ({ value: i.id, label: i.name }))}
                  placeholder="Search container..."
                  empty="No containers found"
                />
              </FormField>

              <SpaceBetween size="s" direction="horizontal">
                <FormField label="Size (L)">
                  <Input value={sizeL} onChange={({ detail }) => setSizeL(detail.value)} placeholder="0.33" />
                </FormField>
              </SpaceBetween>

              {/* Components list */}
              <Box>
                <Header variant="h3">Per-container components</Header>
                {!components?.length ? (
                  <i>None yet</i>
                ) : (
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {components.map((c, i) => (
                      <li key={c.item.id + '-' + i}>
                        {c.amountPerContainer} {c.unitOverride ?? c.item.unit} × {c.item.name}{' '}
                        <Button variant="inline-link" onClick={() => removeComponent(i)}>
                          Remove
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}

                {/* Add component row */}
                <div style={{ marginTop: 8 }}>
                  <SpaceBetween size="s" direction="horizontal">
                    <Autosuggest
                      onChange={({ detail }) => setCompItemId(detail.value)}
                      value={nameOf(compItemId)}
                      options={perContainerItems.map((i) => ({ value: i.id, label: i.name }))}
                      placeholder="Component (per-container item)"
                      empty="No per-container items found"
                    />
                    <Input
                      value={compAmount}
                      onChange={({ detail }) => setCompAmount(detail.value)}
                      placeholder="Amount per container (e.g. 1)"
                    />
                    <Input
                      value={compUnitOverride}
                      onChange={({ detail }) => setCompUnitOverride(detail.value)}
                      placeholder="Unit override (optional)"
                    />
                    <Button onClick={addComponent} iconName="add-plus">
                      Add
                    </Button>
                  </SpaceBetween>
                </div>
              </Box>
            </SpaceBetween>
          </Modal>
        </div>
      </ContentLayout>
    </Loader>
  );
};

export default RecipePage;
